import dayjs from "dayjs";
import type { ReactNode } from "react";
import FilterActions from "./FilterActions";
import type { DateRange, DatePreset } from "../../constants/config/filter";

interface DatePickerFilterProps {
  value: DateRange | undefined;
  onChange: (value: DateRange | undefined) => void;
  onApply: () => void;
  onClear: () => void;
  presets?: DatePreset[];
  /** Extra options shown between the date fields and the footer. */
  beforeActions?: ReactNode;
}

/** Explicit date fields match the reference and keep the existing range contract. */
export default function DatePickerFilter({
  value,
  onChange,
  onApply,
  onClear,
  presets = [],
  beforeActions,
}: DatePickerFilterProps) {
  const from = value?.from ?? "";
  const to = value?.to ?? "";
  // An undefined range means all time and still allows Include Not Started.
  // Incomplete custom ranges must not be sent as empty date parameters.
  const isValid = value === undefined || (!!from && !!to && from <= to);
  return (
    <div className="et-date-filter">
      <div className="et-date-presets">
        {presets.map((preset) => {
          const dates = preset
            .getRange()
            .map((date) => dayjs(date).format("YYYY-MM-DD"));
          // A relative preset stays selected by id so the URL re-resolves it
          // against today; a hand-picked range with the same dates is fixed.
          const selected = value?.preset === preset.value;
          return (
            <button
              type="button"
              key={preset.value}
              aria-pressed={selected}
              onClick={() =>
                onChange({ from: dates[0], to: dates[1], preset: preset.value })
              }
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="et-date-fields">
        <label>
          From
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(event) => onChange({ from: event.target.value, to })}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(event) => onChange({ from, to: event.target.value })}
          />
        </label>
      </div>
      {beforeActions}
      <FilterActions
        onApply={onApply}
        onClear={onClear}
        isApplyDisabled={!isValid}
      />
    </div>
  );
}
